import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Stack,
  Paper,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMember } from '../contexts/MemberContext';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CashierHeader from '../components/CashierHeader';

/**
 * Cashier Coupon Page
 * Shows the selected coupon details to the cashier for verification
 */
const CashierCouponPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { member, selectedOffer, setSelectedOffer, orientation, setOrientation } = useMember();
  const cashierPin = location.state?.cashierPin;

  useEffect(() => {
    if (orientation === 1) {
      // the device was flipped over so navigate to the member dashboard
      navigate('/member-dashboard');
    }
  }, [orientation, navigate]);

  // Redirect if no offer is selected
  useEffect(() => {
    if (!selectedOffer) {
      navigate('/cashier-transaction', { state: { cashierPin } });
    }
  }, [selectedOffer, navigate, cashierPin]);

  // Handle user icon click to switch to customer side
  const handlePersonClick = () => {
    setOrientation(1);
  };

  // Handle continuing to transaction
  const handleContinueToTransaction = () => {
    // Clear the selected offer and navigate to transaction page
    setSelectedOffer(null);
    navigate('/cashier-transaction', { state: { cashierPin } });
  };

  // Handle applying the coupon
  const handleApplyCoupon = () => {
    // Navigate to transaction page with the coupon still selected
    navigate('/cashier-transaction', { state: { cashierPin, applyCoupon: true } });
  };

  if (!selectedOffer) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      py: 4
    }}>
      {/* Header */}
      <CashierHeader />

      {/* Main content */}
      <Box flexGrow={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Stack spacing={3} alignItems="center">
            {/* Title */}
            <Typography variant="h4" component="h1" fontWeight="bold" textAlign="center">
              {selectedOffer.name}
            </Typography>

            {/* Coupon Details */}
            <Box textAlign="center" width="100%">
              {selectedOffer.cashier_directions && selectedOffer.cashier_directions !== '' ? (
                <Typography variant="body1" color="text.secondary" paragraph>
                  {selectedOffer.cashier_directions}
                </Typography>
              ) : (
                <Typography variant="body1" color="text.secondary" paragraph>
                  No additional instructions for this coupon.
                </Typography>
              )}
            </Box>

            {/* Cashier Image */}
            {selectedOffer.cashier_image && (
              <Box textAlign="center" width="100%">
                <img src={selectedOffer.cashier_image} style={{ maxWidth: '100%', maxHeight: '150px' }} />
              </Box>
            )}

            {/* Action buttons */}
            <Stack direction="row" spacing={2} sx={{ mt: 4, width: '100%' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handleApplyCoupon}
                disabled={(selectedOffer.point_value || 0) > member.current_points}
              >
                Finished
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {/* Footer */}
      <Box pb={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body3" color="text.secondary">
          powered by
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <CinchLogo
            width={75}
            style={{ marginRight: '4px' }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default CashierCouponPage;